import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import useModuleProgress from '../hooks/useModuleProgress';
import '../styles/pages/_module-pages.scss';
import '../styles/pages/_learn-assessments.scss';
import '../styles/pages/_quiz-completion.scss';

interface QuizQuestion {
  question: string;
  options: string[];
  correctAnswer: number;
  explanation: string;
}

const quizQuestions: QuizQuestion[] = [
  {
    question: "What is the core essence of mindfulness according to this module?",
    options: [
      "Thinking about multiple things at once",
      "Being fully present and aware in the current moment",
      "Planning for the future constantly",
      "Dwelling on past experiences"
    ],
    correctAnswer: 1,
    explanation: "Mindfulness is about being fully present and aware in the current moment. It involves paying attention to your thoughts, feelings, and surroundings without judgment."
  },
  {
    question: "According to the module, cultivating presence and awareness helps with:",
    options: [
      "Avoiding all emotions and feelings",
      "Reducing stress and improving emotional regulation",
      "Making you less aware of your surroundings",
      "Increasing anxiety and worry"
    ],
    correctAnswer: 1,
    explanation: "Cultivating presence and awareness helps reduce stress and improve emotional regulation. When we're mindful, we can respond to situations more calmly rather than reacting impulsively."
  },
  {
    question: "What is the difference between mindfulness and meditation?",
    options: [
      "They are exactly the same thing",
      "Mindfulness is a way of being present, while meditation is a formal practice",
      "Meditation is only for religious people",
      "Mindfulness requires special equipment"
    ],
    correctAnswer: 1,
    explanation: "Mindfulness is a way of being present in any moment, while meditation is a formal practice that cultivates mindfulness. You can be mindful throughout your day, not just during meditation."
  },
  {
    question: "The module suggests that mindfulness can be practiced through:",
    options: [
      "Only sitting meditation for hours",
      "Simple activities like mindful breathing, eating, and walking",
      "Ignoring your body and surroundings",
      "Multitasking more efficiently"
    ],
    correctAnswer: 1,
    explanation: "Mindfulness can be practiced through simple, everyday activities like mindful breathing, eating, and walking. These practices help bring awareness to ordinary moments."
  },
  {
    question: "What is the benefit of non-judgmental awareness in mindfulness?",
    options: [
      "It makes you critical of yourself and others",
      "It allows you to observe thoughts and feelings without criticism",
      "It prevents you from learning from experiences",
      "It increases self-judgment"
    ],
    correctAnswer: 1,
    explanation: "Non-judgmental awareness allows you to observe your thoughts and feelings without criticism. This creates space for self-compassion and clearer understanding of your experiences."
  }
];

const SpiritualModule3QuizPage: React.FC = () => {
  const navigate = useNavigate();
  const { currentStep, setCurrentStep, isLoading, saveProgress } = useModuleProgress('spiritualM3');
  const [currentQuestion, setCurrentQuestion] = useState<number>(0);
  const [selectedAnswer, setSelectedAnswer] = useState<number | null>(null);
  const [showExplanation, setShowExplanation] = useState(false);
  const [responses, setResponses] = useState<{[key: number]: number}>({});
  const [showCompletion, setShowCompletion] = useState(false);

  const handleAnswerSelect = (answerIndex: number) => {
    if (responses[currentQuestion] === undefined) {
      setSelectedAnswer(answerIndex);
      setShowExplanation(true);
      setResponses(prev => ({...prev, [currentQuestion]: answerIndex}));
    }
  };

  const handleNext = () => {
    if (currentQuestion < quizQuestions.length - 1) {
      const nextQuestion = currentQuestion + 1;
      setCurrentQuestion(nextQuestion);
      setSelectedAnswer(null);
      setShowExplanation(false);
      saveProgress(nextQuestion + 1);
    } else {
      saveProgress(quizQuestions.length, true);
      setShowCompletion(true);
    }
  };

  const handleBack = () => {
    if (currentQuestion > 0) {
      const prevQuestion = currentQuestion - 1;
      const previousResponse = responses[prevQuestion];
      setCurrentQuestion(prevQuestion);
      setSelectedAnswer(previousResponse ?? null);
      setShowExplanation(previousResponse !== undefined);
      saveProgress(prevQuestion + 1);
    }
  };

  const handleExit = () => {
    navigate('/panel/learn/spiritual-resources');
  };

  if (showCompletion) {
    return (
      <div className="quiz-completion-section">
        <h2 className="completion-title">
          <span>Congratulations!</span>
        </h2>
        <p className="completion-subtitle">
          You've successfully completed the Module 3 quiz!
        </p>

        <div className="completion-card">
          <div className="achievement-section">
            <h3 className="section-title">
              <span>Key Achievements</span>
            </h3>
            <p className="achievement-intro">You've demonstrated a great understanding of:</p>
            <ul className="achievement-list">
              <li>
                <span className="check">✓</span>
                <span>The practice of mindfulness</span>
              </li>
              <li>
                <span className="check">✓</span>
                <span>Cultivating presence and awareness</span>
              </li>
              <li>
                <span className="check">✓</span>
                <span>Non-judgmental observation</span>
              </li>
            </ul>
          </div>

          <div className="next-steps-section">
            <h3 className="section-title">
              <span>Next Steps</span>
            </h3>
            <p>Continue your journey by exploring compassion and expanding your heart!</p>
            <p>Ready to deepen your capacity for empathy and kindness?</p>
          </div>

          <button
            className="resource-button"
            onClick={handleExit}
          >
            Return to Spiritual Resources
          </button>
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="module-form" style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh'
      }}>
        <p>Loading your progress...</p>
      </div>
    );
  }

  const progress = ((currentQuestion + 1) / quizQuestions.length) * 100;
  const currentQuiz = quizQuestions[currentQuestion];

  return (
    <div className="module-form">
      <div className="module-container">
        <div className="module-left spiritual-panel">
          <div className="module-header">
            <h1>Module 3 Quiz</h1>
            <p className="module-subtitle">Meaning and Purpose – Finding Your Path</p>
            <hr className="module-divider" />
            <p className="module-tool-info">
              Let's review what we've learned in a stress-free way.
              There's no scoring - this is just to reinforce the main ideas!
            </p>
          </div>
        </div>

        <div className="assessment-right-panel">
          <div className="assessment-content">
            <div className="module-progress">
              <div className="progress-bar">
                <div className="progress-fill" style={{ width: `${progress}%` }}></div>
              </div>
              <span className="progress-text">
                Question {currentQuestion + 1} of {quizQuestions.length}
              </span>
            </div>

            <div className="question-section">
              <h2 className="question-title">
                {currentQuiz.question}
              </h2>
              <p className="question-instruction">
                Please select the answer that best matches your understanding.
              </p>

              <div className="answer-options">
                {currentQuiz.options.map((option, index) => (
                  <label key={index} className="answer-option">
                    <input
                      type="radio"
                      name={`question-${currentQuestion}`}
                      value={index}
                      checked={selectedAnswer === index}
                      onChange={() => handleAnswerSelect(index)}
                      disabled={selectedAnswer !== null}
                    />
                    <span className="option-text">{option}</span>
                  </label>
                ))}
              </div>

              {showExplanation && (
                <div className="explanation">
                  <h3>{selectedAnswer === currentQuiz.correctAnswer ? 'Correct!' : 'Remember:'}</h3>
                  <p>{currentQuiz.explanation}</p>
                </div>
              )}
            </div>

            <div className="navigation-buttons">
              <button
                className="btn-secondary"
                onClick={currentQuestion === 0 ? handleExit : handleBack}
              >
                {currentQuestion === 0 ? 'Exit Quiz' : 'Previous Question'}
              </button>

              {currentQuestion < quizQuestions.length - 1 ? (
                <button
                  className="btn-primary"
                  onClick={handleNext}
                  disabled={selectedAnswer === null}
                >
                  Next Question
                </button>
              ) : (
                <button
                  className="btn-primary"
                  onClick={handleNext}
                  disabled={selectedAnswer === null}
                >
                  Complete Quiz
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SpiritualModule3QuizPage;

